import React, { useEffect, useState } from 'react';
import Portal from './Portal';
import { ChevronLeft, ChevronRight, X, Trash2, Download } from 'lucide-react';

/**
 * Modal reutilizável para visualização expandida de imagens.
 * Usado na galeria da Home e nas fotos do perfil do pet.
 *
 * @param {boolean} isOpen - Se o modal está aberto
 * @param {function} onClose - Callback ao fechar
 * @param {object} selectedImage - Imagem atual (deve ter .url)
 * @param {array} images - Lista de imagens para navegação
 * @param {function} onNavigate - (direction: 'prev' | 'next') => void
 * @param {function} [onDelete] - Callback ao excluir (exibe botão de excluir)
 * @param {function} [onImageClick] - Callback ao clicar na imagem (ex: ir para página do pet)
 * @param {string} [imageClickTitle] - Título/tooltip do clique na imagem
 * @param {string} [imageAlt] - Alt da imagem (default: 'Foto em tamanho expandido')
 * @param {boolean} [showDownload] - Exibir botão de baixar imagem (default: false)
 */
const GalleryImageModal = ({
  isOpen,
  onClose,
  selectedImage,
  images = [],
  onNavigate,
  onDelete,
  onImageClick,
  imageClickTitle,
  imageAlt = 'Foto em tamanho expandido',
  showDownload = false
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onNavigate?.('prev');
          break;
        case 'ArrowRight':
          onNavigate?.('next');
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const savedScrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.documentElement.style.overflow = '';
      if (savedScrollY) window.scrollTo(0, parseInt(savedScrollY || '0', 10) * -1);
    };
  }, [isOpen, onClose, onNavigate]);

  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen) setDownloading(false);
  }, [isOpen]);

  if (!isOpen || !selectedImage) return null;

  const hasNavigation = images.length > 1;

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    const fallbackName = selectedImage.nome && /\.(jpe?g|png|gif|webp)$/i.test(selectedImage.nome)
      ? selectedImage.nome
      : (selectedImage.nome ? `${selectedImage.nome}.jpg` : 'imagem.jpg');
    try {
      const response = await fetch(selectedImage.url, { mode: 'cors' });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fallbackName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(selectedImage.url, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Portal>
      <div
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-2 sm:p-3 overflow-hidden overscroll-none"
        onClick={onClose}
      >
        <div className="relative flex items-center justify-center min-h-full min-w-0">
          <div
            className="relative aspect-[3/5] flex flex-col p-3 sm:p-4 rounded-xl bg-gray-200 dark:bg-gray-800 shadow-2xl flex-shrink-0 max-h-[100vh]"
            style={{ width: 'min(90vw, 60vh)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Faixa superior: lixeira+download (esq) | navegação (centro) | fechar (dir) */}
            <div className="relative flex-shrink-0 flex items-center h-12 sm:h-14 mb-2 gap-2 w-full">
              <div className="flex flex-1 justify-start items-center gap-2 min-w-0">
                {onDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(selectedImage);
                    }}
                    className="flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-red-500 bg-opacity-80 text-white rounded-full hover:bg-opacity-100 transition-all duration-200"
                    title="Excluir foto"
                  >
                    <Trash2 className="w-6 h-6" />
                  </button>
                )}
                {showDownload && (
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                    title={downloading ? 'Baixando...' : 'Baixar imagem'}
                  >
                    {downloading ? (
                      <span className="animate-spin inline-block w-6 h-6 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <Download className="w-6 h-6" />
                    )}
                  </button>
                )}
              </div>
              {hasNavigation && (
                <div className="flex flex-shrink-0 justify-center items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate('prev');
                    }}
                    className={`flex-shrink-0 flex items-center justify-center h-10 bg-black bg-opacity-50 text-white rounded-lg hover:bg-opacity-75 transition-all duration-200 ${showDownload ? 'w-14 sm:w-24' : 'w-24'}`}
                    title="Imagem anterior"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate('next');
                    }}
                    className={`flex-shrink-0 flex items-center justify-center h-10 bg-black bg-opacity-50 text-white rounded-lg hover:bg-opacity-75 transition-all duration-200 ${showDownload ? 'w-14 sm:w-24' : 'w-24'}`}
                    title="Próxima imagem"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              )}
              <div className="flex flex-1 justify-end items-center min-w-0">
                <button
                  onClick={onClose}
                  className="flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-200"
                  title="Fechar"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Área da imagem */}
            <div
              className={`flex-1 min-h-0 rounded-lg overflow-hidden bg-black/20 flex items-center justify-center ${onImageClick ? 'cursor-pointer' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (onImageClick) onImageClick(selectedImage);
              }}
              title={imageClickTitle}
            >
              <img
                src={selectedImage.url}
                alt={imageAlt}
                className="w-full h-full object-cover pointer-events-none"
              />
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default GalleryImageModal;
